"use client"

import Link from "next/link"
import { Menu, X } from "lucide-react"
import { ThemeToggle } from "./theme-toggle"
import { useState, useEffect } from "react"
import Image from "next/image"
import { useTheme } from "next-themes"
import { Logo } from "./logo"

export function PublicNavigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { resolvedTheme } = useTheme()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  if (!isClient) {
    return null
  }

  return (
    <nav className={`relative z-50`}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <Link
            href="/"
            className="flex items-center space-x-2"
            data-analytics-category="PUBLIC_NAV"
            data-analytics-action="CLICK"
            data-analytics-name="PUBLIC_NAV_HOME_LOGO"
          >
            <Logo />
            <span className="font-bold text-xl">The HIIT PIT</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-4 items-center">

            <Link
              href="/faq"
              className="hover:text-[#8E82FF] transition-colors"
              data-analytics-category="PUBLIC_NAV"
              data-analytics-action="CLICK"
              data-analytics-name="PUBLIC_NAV_FAQ"
            >
              FAQ
            </Link>
            <Link
              href="/hiit"
              className="hover:text-[#8E82FF] transition-colors"
              data-analytics-category="PUBLIC_NAV"
              data-analytics-action="CLICK"
              data-analytics-name="PUBLIC_NAV_ABOUT_HIIT"
            >
              About HIIT
            </Link>


            <ThemeToggle />


            <Link
              href="/sign-in"
              className="px-4 py-2 rounded-full bg-[#8E82FF] text-white hover:bg-opacity-90 transition-colors"
              data-analytics-category="CTA_ENGAGEMENT"
              data-analytics-action="CLICK"
              data-analytics-name="PUBLIC_CTA_SIGN_IN_DESKTOP"
            >
              Sign In
            </Link>

          </div>

          {/* Mobile Navigation */}
          <div className="flex items-center space-x-2 md:hidden">
            <ThemeToggle />
            <Link
              href="/sign-in"
              className="px-3 py-1.5 text-sm rounded-full bg-[#8E82FF] text-white hover:bg-opacity-90 transition-colors"
              data-analytics-category="CTA_ENGAGEMENT"
              data-analytics-action="CLICK"
              data-analytics-name="PUBLIC_CTA_SIGN_IN_MOBILE"
            >
              Sign In
            </Link>


            <button
              onClick={toggleMobileMenu}
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              data-analytics-category="PUBLIC_NAV"
              data-analytics-action={mobileMenuOpen ? "CLOSE" : "OPEN"}
              data-analytics-name={mobileMenuOpen ? "PUBLIC_NAV_MOBILE_MENU_CLOSE" : "PUBLIC_NAV_MOBILE_MENU_OPEN"}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          id="mobile-menu"
          className="md:hidden fixed inset-0 z-40 bg-white dark:bg-black"
          aria-labelledby="mobile-menu-heading"
          role="dialog"
          aria-modal="true"
        >
          <h2 id="mobile-menu-heading" className="sr-only">
            Mobile navigation menu
          </h2>
          <div className="container mx-auto px-6 py-4">
            <div className="flex justify-between items-center mb-8">
              <Link
                href="/"
                className="flex items-center space-x-2"
                data-analytics-category="PUBLIC_NAV"
                data-analytics-action="CLICK"
                data-analytics-name="PUBLIC_NAV_HOME_MOBILE_MENU"
              >
                <div className="h-10 w-10 relative">
                  <Image
                    src={resolvedTheme === "dark" ? "/images/thehiitpit-logo-light.svg" : "/images/thehiitpit-logo.svg"}
                    alt="The HIIT PIT Logo"
                    width={40}
                    height={40}
                    className="object-contain"
                  />
                </div>
                <span className="font-bold text-xl">The HIIT PIT</span>
              </Link>

              <div className="flex items-center space-x-2">
                <ThemeToggle />


                <Link
                  href="/sign-in"
                  className="px-3 py-1.5 text-sm rounded-full bg-[#8E82FF] text-white hover:bg-opacity-90 transition-colors"
                  data-analytics-category="CTA_ENGAGEMENT"
                  data-analytics-action="CLICK"
                  data-analytics-name="PUBLIC_CTA_SIGN_IN_MOBILE_MENU"
                >
                  Sign In
                </Link>


                <button
                  onClick={toggleMobileMenu}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Close menu"
                  data-analytics-category="PUBLIC_NAV"
                  data-analytics-action="CLOSE"
                  data-analytics-name="PUBLIC_NAV_MOBILE_MENU_CLOSE_ICON"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex flex-col space-y-6 text-xl">
              <Link
                href="/faq"
                className="py-2 hover:text-[#8E82FF] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-analytics-category="PUBLIC_NAV"
                data-analytics-action="CLICK"
                data-analytics-name="PUBLIC_NAV_FAQ_MOBILE_MENU"
              >
                FAQ
              </Link>
              <Link
                href="/hiit"
                className="py-2 hover:text-[#8E82FF] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-analytics-category="PUBLIC_NAV"
                data-analytics-action="CLICK"
                data-analytics-name="PUBLIC_NAV_ABOUT_HIIT_MOBILE_MENU"
              >
                About HIIT
              </Link>
              <Link
                href="/sign-in"
                className="py-2 hover:text-[#8E82FF] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-analytics-category="CTA_ENGAGEMENT"
                data-analytics-action="CLICK"
                data-analytics-name="PUBLIC_CTA_SIGN_IN_MOBILE_MENU_LINK"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
